import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, ChevronUp, Smile, Users } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { canViewInsights } from "@/shared/insightsAccess";
import { GlobalSearch } from "@/shared/layout/globalSearch";
import { JelloShotLogo } from "@/shared/layout/jelloShotLogo";
import { NavLink } from "@/shared/layout/navLink";
import { ProjectsNavItem } from "@/shared/layout/projectsNavItem";
import { CreateProjectModal } from "@/shared/components/createProjectModal";
import { ReportProblemDialog } from "@/pages/insights/reportProblemDialog";
import { UserAvatar } from "@/shared/ui/userAvatar";

const DIRECTORY_ITEMS = [
  { to: "/clients", label: "Clients", icon: Smile },
  { to: "/contacts", label: "Crew", icon: Users },
] as const;

interface AppSidebarProps {
  onNavigate?: () => void;
  showLogo?: boolean;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  onNavigate,
  showLogo = true,
}) => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  const profileName = user?.full_name?.trim() || user?.email || "Account";

  const handleLogout = () => {
    setAccountOpen(false);
    logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!accountOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (accountRef.current?.contains(target)) {
        return;
      }
      setAccountOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [accountOpen]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {showLogo ? (
        <div className="px-5 pb-5 pt-6">
          <Link
            to="/projects"
            aria-label="JelloShot home"
            onClick={onNavigate}
            className="inline-flex"
          >
            <JelloShotLogo className="h-8 sm:h-8" />
          </Link>
        </div>
      ) : (
        <div className="pt-3" />
      )}

      <div className="px-4 pb-4">
        <GlobalSearch variant="bar" />
      </div>

      <nav aria-label="Primary" className="flex flex-col gap-0.5 px-3">
        <ProjectsNavItem
          {...(onNavigate ? { onNavigate } : {})}
          onCreateProject={() => {
            onNavigate?.();
            setCreateProjectOpen(true);
          }}
        />
        {DIRECTORY_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              variant="sidebar"
              {...(onNavigate ? { onClick: onNavigate } : {})}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
        {canViewInsights(user?.email) ? (
          <NavLink
            to="/insights"
            variant="sidebar"
            {...(onNavigate ? { onClick: onNavigate } : {})}
          >
            <BarChart3 className="h-4 w-4 shrink-0" />
            Master
          </NavLink>
        ) : null}
      </nav>

      <div className="mt-auto border-t border-border p-3">
        {user ? (
          <div ref={accountRef} className="relative">
            {accountOpen ? (
              <div className="absolute inset-x-0 bottom-full mb-2 overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg">
                <p className="truncate px-3 py-2 text-xs text-muted-foreground">
                  {user.email}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAccountOpen(false);
                    setReportOpen(true);
                  }}
                  className="flex w-full items-center rounded-md px-3 py-2 text-sm text-foreground hover:bg-accent"
                >
                  Report a problem
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center rounded-md px-3 py-2 text-sm text-foreground hover:bg-accent"
                >
                  Log off
                </button>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => setAccountOpen((current) => !current)}
              aria-expanded={accountOpen}
              aria-haspopup="menu"
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-foreground/5"
            >
              <UserAvatar
                name={user.full_name}
                email={user.email}
                size="sm"
                className="bg-gradient-to-b from-[#F4845A] to-[#E24C2A]"
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {profileName}
              </span>
              <ChevronUp
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                  accountOpen ? "" : "rotate-180"
                }`}
              />
            </button>
          </div>
        ) : null}
        <Link
          to="/terms"
          {...(onNavigate ? { onClick: onNavigate } : {})}
          className="mt-2 block px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Terms
        </Link>
      </div>
      <ReportProblemDialog open={reportOpen} onOpenChange={setReportOpen} />
      <CreateProjectModal
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
        onCreated={(projectId) => {
          navigate(`/projects/${projectId}/shotlist`);
        }}
      />
    </div>
  );
};
